import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import styled, { css, keyframes } from 'styled-components';
import { wiki, c, fadeIn, SkeletonLine } from './wikiStyles';
import {
  useWikiConfig,
  useWikiPage,
  useWikiAdmin,
  useWikiProofread,
  useScrollSpy,
  useReviewComments,
  regeneratePage,
  slugToTitle,
  processContent,
} from './useWiki';
import WikiNotFound from './WikiNotFound';

/* ── Helpers ────────────────────────────────────────────── */

function getChildText(node) {
  if (!node) return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getChildText).join('');
  if (node.props && node.props.children)
    return getChildText(node.props.children);
  return '';
}

function textToId(text) {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-');
}

function timeAgo(iso) {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/* ── Styled: Layout ─────────────────────────────────────── */

const PageWrap = styled.div`
  animation: ${fadeIn} 0.4s ease;
`;

/* ── Styled: Page Hero ──────────────────────────────────── */

const PageHero = styled.div`
  border-bottom: 1px solid ${c.border};
`;

const PageHeroInner = styled.div`
  max-width: ${wiki.maxPage};
  margin: 0 auto;
  padding: 1.25rem 2.5rem 1rem;

  @media (max-width: 1080px) {
    padding: 1rem 2rem;
  }
  @media (max-width: 768px) {
    padding: 0.75rem 1.25rem;
  }
`;

const Breadcrumb = styled.nav`
  font-family: ${wiki.fonts.ui};
  font-size: 0.75rem;
  color: ${c.textMuted};
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.5rem;

  a {
    color: ${c.textMuted};
    text-decoration: none;
    &:hover {
      color: ${c.accent};
    }
  }
`;

const Sep = styled.span`
  opacity: 0.5;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.4rem;
`;

const PageTitle = styled.h1`
  font-family: ${wiki.fonts.heading};
  font-size: 2.2rem;
  font-weight: 700;
  color: ${c.text};
  line-height: 1.2;
  letter-spacing: -0.01em;
  flex: 1;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  font-family: ${wiki.fonts.ui};
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
  flex-shrink: 0;
  margin-top: 0.45rem;

  ${(p) =>
    p.$approved
      ? css`
          background: ${c.verifiedSoft};
          color: ${c.verified};
          border: 1px solid ${c.verified};
        `
      : css`
          background: ${c.accentSoft};
          color: ${c.accent};
          border: 1px solid color-mix(in srgb, ${c.accent} 30%, transparent);
        `}
`;

const StatusIcon = styled.span`
  font-size: 0.85rem;
  line-height: 1;
`;

const MetaLine = styled.div`
  font-family: ${wiki.fonts.ui};
  font-size: 0.72rem;
  color: ${c.textMuted};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  letter-spacing: 0.01em;
`;

const MetaDot = styled.span`
  opacity: 0.4;
`;

const DharaLogoInline = styled.img`
  height: 16px;
  width: auto;
  vertical-align: middle;
  opacity: 0.75;
`;

const NotProofreadBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.6rem 1.1rem;
  margin-top: 0.75rem;
  background: ${c.accentSoft};
  border: 1px solid color-mix(in srgb, ${c.accent} 25%, transparent);
  border-left: 3px solid ${c.accent};
  border-radius: 0 ${wiki.radiusSm} ${wiki.radiusSm} 0;
  font-family: ${wiki.fonts.ui};
  font-size: 0.8rem;
  color: ${c.accent};
  line-height: 1.4;
`;

const BannerIcon = styled.span`
  font-size: 1rem;
  flex-shrink: 0;
  line-height: 1.3;
`;

/* ── Styled: Content Layout ─────────────────────────────── */

const ContentLayout = styled.div`
  max-width: ${wiki.maxPage};
  margin: 0 auto;
  padding: 1.75rem 2.5rem 0;
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 0 0;
  align-items: start;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
    padding: 1.5rem 2rem 0;
  }
  @media (max-width: 768px) {
    padding: 1.25rem 1.25rem 0;
  }
`;

const ContentMain = styled.div`
  min-width: 0;
  padding-left: 2.5rem;

  @media (max-width: 1080px) {
    padding-left: 0;
  }
`;

/* ── Styled: Article Content ─────────────────────────────── */

const Article = styled.article`
  font-family: ${wiki.fonts.body};
  font-size: 1.125rem;
  line-height: 1.85;
  color: ${c.text};
  counter-reset: wiki-section;

  > p:first-of-type {
    font-size: 1.2rem;
    line-height: 2;
    color: ${c.textSec};
    padding: 1.25rem 1.5rem;
    background: linear-gradient(135deg, ${c.accentSoft} 0%, transparent 55%);
    border-left: 3px solid ${c.accent};
    border-radius: 0 ${wiki.radiusSm} ${wiki.radiusSm} 0;
    margin-bottom: 2.25rem;
    position: relative;
  }

  > p:first-of-type::first-letter {
    float: left;
    font-family: ${wiki.fonts.heading};
    font-size: 3.6rem;
    line-height: 0.85;
    padding-right: 0.45rem;
    padding-top: 0.05rem;
    color: ${c.accent};
    font-weight: 700;
  }

  h2 {
    counter-increment: wiki-section;
    font-family: ${wiki.fonts.heading};
    font-size: 1.65rem;
    font-weight: 600;
    margin-top: 3.5rem;
    margin-bottom: 1.25rem;
    padding: 0.7rem 1.25rem;
    border-left: 4px solid ${c.accent};
    background: linear-gradient(90deg, ${c.accentSoft}, transparent 85%);
    border-radius: 0 ${wiki.radiusSm} ${wiki.radiusSm} 0;
    color: ${c.text};
    line-height: 1.35;
    position: relative;

    &::before {
      content: counter(wiki-section, decimal-leading-zero);
      position: absolute;
      right: 1.25rem;
      top: 50%;
      transform: translateY(-50%);
      font-family: ${wiki.fonts.heading};
      font-size: 3.2rem;
      font-weight: 700;
      color: ${c.accent};
      opacity: 0.07;
      pointer-events: none;
      line-height: 1;
    }
  }

  h2:not(:first-of-type)::after {
    content: '◈\\00a0\\00a0\\00a0◇\\00a0\\00a0\\00a0◈';
    position: absolute;
    top: -2.25rem;
    left: 0;
    right: 0;
    text-align: center;
    font-size: 0.55rem;
    color: ${c.accent};
    opacity: 0.3;
    letter-spacing: 0.2em;
    pointer-events: none;
  }

  h3 {
    font-family: ${wiki.fonts.heading};
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 2.5rem;
    margin-bottom: 0.75rem;
    color: ${c.textSec};
    line-height: 1.35;
    padding-left: 1.1rem;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0.55em;
      width: 6px;
      height: 6px;
      background: ${c.accent};
      border-radius: 50%;
    }
  }

  h4 {
    font-family: ${wiki.fonts.heading};
    font-weight: 600;
    margin-top: 1.75rem;
    margin-bottom: 0.5rem;
    color: ${c.textSec};
    text-transform: uppercase;
    font-size: 0.95rem;
    letter-spacing: 0.03em;
  }

  h5 {
    font-family: ${wiki.fonts.heading};
    font-weight: 600;
    margin-top: 1.5rem;
    margin-bottom: 0.4rem;
    color: ${c.textSec};
    font-size: 0.9rem;
    font-style: italic;
  }

  p {
    margin-bottom: 1.5rem;
  }

  em {
    font-style: italic;
    color: ${c.emColor};
    background: ${c.emBg};
    padding: 0.05rem 0.3rem;
    border-radius: 3px;
  }

  strong {
    font-weight: 600;
    color: ${c.text};
  }

  li > strong:first-child {
    color: ${c.text};
    background: linear-gradient(135deg, ${c.accentSoft}, ${c.accentSoft});
    padding: 0.1rem 0.55rem;
    border-radius: 4px;
    border: 1px solid color-mix(in srgb, ${c.accent} 9%, transparent);
    font-weight: 700;
    font-size: 0.98em;
  }

  ul,
  ol {
    margin: 1.25rem 0 1.5rem;
    padding-left: 1.75rem;
  }

  li {
    margin-bottom: 0.65rem;
    line-height: 1.75;
  }

  ul {
    list-style: none;
    li {
      position: relative;
      padding-left: 0.5rem;
      &::before {
        content: '\\25C6';
        position: absolute;
        left: -1.25rem;
        top: 0.1em;
        color: ${c.accent};
        font-size: 0.5rem;
      }
    }

    ul {
      margin-top: 0.4rem;
      margin-bottom: 0.4rem;
      padding-left: 1.5rem;
      li::before {
        content: '\\25CB';
        font-size: 0.45rem;
      }

      ul {
        li::before {
          content: '\\25AA';
          font-size: 0.4rem;
        }
      }
    }
  }

  ol li::marker {
    color: ${c.accent};
    font-weight: 700;
    font-family: ${wiki.fonts.heading};
  }

  blockquote {
    margin: 2rem 0;
    padding: 1.5rem 1.75rem 1.5rem 2rem;
    border-left: 4px solid ${c.accent};
    background: ${c.accentSoft};
    border-radius: 0 ${wiki.radius} ${wiki.radius} 0;
    font-style: italic;
    color: ${c.textSec};
    position: relative;
    overflow: hidden;

    &::before {
      content: '\\201C';
      position: absolute;
      top: 0.25rem;
      left: 0.6rem;
      font-family: ${wiki.fonts.heading};
      font-size: 3.5rem;
      color: ${c.accent};
      opacity: 0.18;
      line-height: 1;
    }

    p:last-child {
      margin-bottom: 0;
    }
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 2rem 0;
    font-size: 0.95rem;
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  th,
  td {
    padding: 0.7rem 1rem;
    text-align: left;
    border-bottom: 1px solid ${c.border};
  }

  th {
    font-family: ${wiki.fonts.ui};
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${c.textMuted};
    background: ${c.bgAlt};
  }

  tr:hover td {
    background: ${c.accentSoft};
  }

  hr {
    border: none;
    text-align: center;
    margin: 3rem auto;
    height: 1px;
    background: linear-gradient(to right, transparent, ${c.border}, transparent);
    overflow: visible;

    &::after {
      content: '✦\\00a0\\00a0◇\\00a0\\00a0✦';
      display: inline-block;
      position: relative;
      top: -0.55em;
      padding: 0 0.75rem;
      background: ${c.bg};
      color: ${c.accent};
      font-size: 0.65rem;
      letter-spacing: 0.1em;
    }
  }

  a:not(.wiki-cite-link) {
    color: ${c.accent};
    text-decoration: none;
    border-bottom: 1px dotted color-mix(in srgb, ${c.accent} 45%, transparent);
    transition: all 0.2s;

    &:hover {
      border-bottom-style: solid;
      color: ${c.accentHover};
    }
  }

  .wiki-cite {
    font-family: ${wiki.fonts.ui};
    font-size: 0.65rem;
    vertical-align: super;
    line-height: 0;
    margin: 0 1px;

    a {
      color: ${c.citation};
      text-decoration: none;
      padding: 1px 2px;
      border-radius: 2px;
      border-bottom: none;
      transition: background 0.2s;

      &:hover {
        background: ${c.citationSoft};
      }
    }
  }

  .wiki-review-hl {
    background: rgba(255, 230, 0, 0.25);
    border-bottom: 2px solid rgba(255, 200, 0, 0.6);
    cursor: pointer;
    border-radius: 2px;
    padding: 0 1px;
  }
`;

/* ── Styled: End-of-article ──────────────────────────────── */

const ArticleEnd = styled.div`
  text-align: center;
  padding: 2.5rem 0 0.5rem;
  color: ${c.accent};
  opacity: 0.3;
  user-select: none;

  &::before {
    content: '';
    display: block;
    width: 60px;
    height: 1px;
    background: linear-gradient(
      to right,
      transparent,
      ${c.accent},
      transparent
    );
    margin: 0 auto 1rem;
  }
`;

const EndMark = styled.span`
  font-size: 1.4rem;
  letter-spacing: 0.5em;
`;

/* ── Styled: Table of Contents ──────────────────────────── */

const TOCSide = styled.aside`
  align-self: stretch;

  @media (max-width: 1080px) {
    display: none;
  }
`;

const TOCSticky = styled.div`
  position: sticky;
  top: calc(${wiki.headerHeight} + 1.25rem);
  padding: 0.25rem 1.75rem 2rem 0;
  border-right: 1px solid ${c.borderLight};
  max-height: calc(100vh - ${wiki.headerHeight} - 2.5rem);
  overflow-y: auto;

  scrollbar-width: thin;
  scrollbar-color: ${c.border} transparent;

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${c.border};
    border-radius: 3px;
  }
`;

const TOCTitle = styled.h4`
  font-family: ${wiki.fonts.ui};
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: ${c.accent};
  margin-bottom: 0.85rem;
  padding-bottom: 0.6rem;
  border-bottom: 2px solid color-mix(in srgb, ${c.accent} 15%, transparent);
`;

const TOCList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const TOCItem = styled.li`
  margin-bottom: 0.05rem;

  a {
    display: block;
    padding: 0.35rem 0.6rem;
    padding-left: ${(p) => (p.$level === 3 ? '1.2rem' : '0.6rem')};
    font-family: ${wiki.fonts.ui};
    font-size: ${(p) => (p.$level === 3 ? '0.72rem' : '0.78rem')};
    line-height: 1.4;
    color: ${(p) => (p.$active ? c.accent : c.textSec)};
    font-weight: ${(p) => (p.$active ? '600' : '400')};
    text-decoration: none;
    border-left: 2px solid
      ${(p) => (p.$active ? c.accent : 'transparent')};
    transition: all 0.2s;

    &:hover {
      color: ${c.accent};
      border-left-color: color-mix(in srgb, ${c.accent} 38%, transparent);
    }
  }
`;

/* ── Styled: References Panel ───────────────────────────── */

const RefsPanel = styled.section`
  margin-top: 3.5rem;
  padding: 2rem 2rem 1.75rem;
  background: linear-gradient(180deg, ${c.bgAlt} 0%, ${c.bg} 100%);
  border-radius: ${wiki.radius};
  border: 1px solid ${c.borderLight};
  border-top: 3px solid ${c.citation};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 2rem;
    right: 2rem;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      color-mix(in srgb, ${c.citation} 19%, transparent),
      transparent
    );
  }
`;

const RefsTitle = styled.h3`
  font-family: ${wiki.fonts.ui};
  font-size: 0.76rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${c.citation};
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: '\\00A7';
    font-size: 1.1rem;
    opacity: 0.65;
  }
`;

const RefList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const refFlash = keyframes`
  0% { background: color-mix(in srgb, ${c.citation} 28%, transparent); }
  100% { background: transparent; }
`;

const RefEntry = styled.li`
  display: flex;
  gap: 0.75rem;
  padding: 0.55rem 0.5rem;
  font-family: ${wiki.fonts.ui};
  font-size: 0.82rem;
  line-height: 1.55;
  color: ${c.textSec};
  border-bottom: 1px solid ${c.borderLight};
  scroll-margin-top: 100px;
  transition: background 0.2s;
  border-radius: ${wiki.radiusSm};

  &:hover {
    background: ${c.citationSoft};
  }

  &:last-child {
    border-bottom: none;
  }

  &.wiki-ref-highlight {
    animation: ${refFlash} 2s ease-out;
    outline: 2px solid ${c.citation};
    outline-offset: -1px;
  }
`;

const RefNum = styled.span`
  flex-shrink: 0;
  font-weight: 700;
  font-size: 0.72rem;
  color: ${c.bgWhite};
  background: ${c.citation};
  min-width: 1.6rem;
  height: 1.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-top: 0.1rem;
`;

/* ── Styled: Admin / Edit ───────────────────────────────── */

const EditTrigger = styled.button`
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  background: ${c.bgWhite};
  border: 1px solid ${c.border};
  color: ${c.textMuted};
  font-family: ${wiki.fonts.ui};
  font-size: 0.78rem;
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.3s;
  z-index: 90;
  box-shadow: 0 2px 12px ${c.shadow};

  &:hover {
    opacity: 1;
    border-color: ${c.accent};
    color: ${c.accent};
    box-shadow: 0 4px 16px ${c.shadowMd};
  }
`;

const AdminBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #1E1C19;
  color: rgba(255, 255, 255, 0.9);
  padding: 0.6rem 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  z-index: 200;
  font-family: ${wiki.fonts.ui};
  font-size: 0.82rem;
  flex-wrap: wrap;
`;

const AdminBtn = styled.button`
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: rgba(255, 255, 255, 0.9);
  padding: 0.35rem 0.9rem;
  border-radius: ${wiki.radiusSm};
  font-family: ${wiki.fonts.ui};
  font-size: 0.78rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.35rem;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.4);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    &:hover {
      background: none;
      border-color: rgba(255, 255, 255, 0.25);
    }
  }
`;

const ApproveBtn = styled(AdminBtn)`
  ${(p) =>
    p.$approved &&
    css`
      background: rgba(143, 168, 126, 0.2);
      border-color: rgba(143, 168, 126, 0.5);
      color: #8fa87e;
    `}
`;

const ReviewBadge = styled.span`
  background: rgba(255, 255, 255, 0.2);
  font-size: 0.65rem;
  padding: 0.1rem 0.4rem;
  border-radius: 9px;
  min-width: 1.1rem;
  text-align: center;
`;

const AdminDivider = styled.span`
  width: 1px;
  height: 1.2rem;
  background: rgba(255, 255, 255, 0.15);
`;

/* ── Styled: Upload Modal ──────────────────────────────── */

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${c.overlay};
  z-index: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  animation: ${fadeIn} 0.2s ease;
`;

const ModalBox = styled.div`
  background: ${c.bgWhite};
  border-radius: ${wiki.radius};
  padding: 2rem 2.25rem;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 16px 64px rgba(0, 0, 0, 0.3);
  position: relative;
`;

/* ── Regeneration overlay ──────────────────────────────── */

const regenSpin = keyframes`
  to { transform: rotate(360deg); }
`;

const regenPulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

const RegenOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${c.overlay};
  z-index: 600;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  animation: ${fadeIn} 0.3s ease;
`;

const RegenSpinner = styled.div`
  width: 56px;
  height: 56px;
  border: 3px solid color-mix(in srgb, ${c.accent} 25%, transparent);
  border-top-color: ${c.accent};
  border-radius: 50%;
  animation: ${regenSpin} 0.9s linear infinite;
`;

const RegenTitle = styled.h3`
  font-family: ${wiki.fonts.heading};
  font-size: 1.35rem;
  font-weight: 600;
  color: ${c.bgWhite};
  margin: 0;
`;

const RegenMsg = styled.p`
  font-family: ${wiki.fonts.ui};
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.75);
  max-width: 360px;
  text-align: center;
  line-height: 1.6;
  margin: 0;
  animation: ${regenPulse} 2s ease-in-out infinite;
`;

const RegenTimer = styled.span`
  font-family: ${wiki.fonts.ui};
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
  font-variant-numeric: tabular-nums;
`;

/* ── Comparison view after regeneration ────────────────── */

const CompareOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${c.bg};
  z-index: 600;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.3s ease;
  overflow: hidden;
`;

const CompareHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 2rem;
  border-bottom: 1px solid ${c.border};
  background: ${c.bgWhite};
  flex-shrink: 0;
`;

const CompareTitle = styled.h3`
  font-family: ${wiki.fonts.heading};
  font-size: 1.2rem;
  font-weight: 600;
  color: ${c.text};
  margin: 0;
`;

const CompareActions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const CompareBtn = styled.button`
  font-family: ${wiki.fonts.ui};
  font-size: 0.85rem;
  padding: 0.5rem 1.25rem;
  border-radius: ${wiki.radiusSm};
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${(p) => (p.$primary ? c.accent : c.border)};
  background: ${(p) => (p.$primary ? c.accent : 'transparent')};
  color: ${(p) => (p.$primary ? c.bgWhite : c.textSec)};

  &:hover {
    opacity: 0.85;
    transform: translateY(-1px);
  }
`;

const CompareTabs = styled.div`
  display: flex;
  border-bottom: 1px solid ${c.border};
  background: ${c.bgWhite};
  flex-shrink: 0;
  padding: 0 2rem;
`;

const CompareTab = styled.button`
  font-family: ${wiki.fonts.ui};
  font-size: 0.85rem;
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  border-bottom: 2px solid ${(p) => (p.$active ? c.accent : 'transparent')};
  color: ${(p) => (p.$active ? c.accent : c.textMuted)};
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: ${c.accent};
  }
`;

const CompareBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;

  & .wiki-compare-content {
    max-width: 800px;
    margin: 0 auto;
    font-family: ${wiki.fonts.body};
    font-size: 1.05rem;
    line-height: 1.8;
    color: ${c.text};

    h2 { font-family: ${wiki.fonts.heading}; font-size: 1.4rem; margin-top: 2rem; color: ${c.text}; }
    h3 { font-family: ${wiki.fonts.heading}; font-size: 1.15rem; margin-top: 1.5rem; color: ${c.text}; }
    p { margin-bottom: 1rem; }
    ul, ol { margin-bottom: 1rem; padding-left: 1.5rem; }
    strong { font-weight: 600; }
    em { font-style: italic; }
    code { font-size: 0.9em; background: ${c.codeBg || 'rgba(0,0,0,0.05)'}; padding: 0.15em 0.35em; border-radius: 3px; }
  }
`;

const CompareLabel = styled.div`
  font-family: ${wiki.fonts.ui};
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${(p) => (p.$new ? c.accent : c.textMuted)};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CompareBadge = styled.span`
  font-size: 0.65rem;
  padding: 0.15rem 0.5rem;
  border-radius: 99px;
  background: ${(p) => (p.$new ? c.accent : c.border)};
  color: ${(p) => (p.$new ? c.bgWhite : c.textMuted)};
`;

const ModalTitle = styled.h3`
  font-family: ${wiki.fonts.heading};
  font-size: 1.25rem;
  font-weight: 600;
  color: ${c.text};
  margin-bottom: 1.25rem;
`;

const ModalClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.3rem;
  color: ${c.textMuted};
  cursor: pointer;
  padding: 0.25rem;
  line-height: 1;

  &:hover {
    color: ${c.text};
  }
`;

const FileDropZone = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1.5rem;
  border: 2px dashed ${c.border};
  border-radius: ${wiki.radiusSm};
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;
  text-align: center;
  font-family: ${wiki.fonts.ui};
  font-size: 0.85rem;
  color: ${c.textMuted};

  &:hover {
    border-color: ${c.accent};
    background: ${c.accentSoft};
  }

  input {
    display: none;
  }
`;

const FileInfo = styled.div`
  font-family: ${wiki.fonts.ui};
  font-size: 0.85rem;
  color: ${c.text};
  padding: 0.6rem 0.75rem;
  background: ${c.bgAlt};
  border-radius: ${wiki.radiusSm};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModeSelector = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
`;

const ModeOption = styled.button`
  flex: 1;
  padding: 0.65rem 0.75rem;
  font-family: ${wiki.fonts.ui};
  font-size: 0.78rem;
  border-radius: ${wiki.radiusSm};
  border: 1.5px solid ${(p) => (p.$active ? c.accent : c.border)};
  background: ${(p) => (p.$active ? c.accentSoft : 'transparent')};
  color: ${(p) => (p.$active ? c.accent : c.textSec)};
  font-weight: ${(p) => (p.$active ? '600' : '400')};
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
  line-height: 1.3;

  &:hover {
    border-color: ${c.accent};
  }
`;

const UploadBtn = styled.button`
  width: 100%;
  padding: 0.75rem;
  font-family: ${wiki.fonts.ui};
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: ${wiki.radiusSm};
  border: none;
  background: ${c.accent};
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${c.accentHover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const UploadStatus = styled.div`
  font-family: ${wiki.fonts.ui};
  font-size: 0.82rem;
  text-align: center;
  padding: 0.75rem;
  border-radius: ${wiki.radiusSm};
  margin-top: 0.75rem;
  background: ${(p) => (p.$error ? 'rgba(200,60,60,0.08)' : c.accentSoft)};
  color: ${(p) => (p.$error ? '#c43c3c' : c.text)};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: ${c.borderLight};
  border-radius: 2px;
  margin-top: 0.75rem;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${(p) => p.$pct || 0}%;
    background: ${c.accent};
    border-radius: 2px;
    transition: width 0.3s ease;
  }
`;

/* ── Styled: Review Mode ────────────────────────────────── */

const ReviewBar = styled.div`
  position: fixed;
  top: calc(${wiki.headerHeight} + 3px);
  left: 0;
  right: 0;
  background: #2d2a26;
  color: rgba(255, 255, 255, 0.85);
  padding: 0.35rem 1rem;
  font-family: ${wiki.fonts.ui};
  font-size: 0.72rem;
  text-align: center;
  z-index: 99;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
`;

const ReviewBarBtn = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.85);
  padding: 0.2rem 0.6rem;
  border-radius: ${wiki.radiusSm};
  font-family: ${wiki.fonts.ui};
  font-size: 0.68rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
`;

const SelectionPopover = styled.div`
  position: fixed;
  z-index: 160;
  background: ${c.bgWhite};
  border: 1px solid ${c.border};
  border-radius: ${wiki.radius};
  box-shadow: 0 8px 32px ${c.shadowMd};
  padding: 0.85rem;
  width: 280px;
  animation: ${fadeIn} 0.15s ease;
`;

const PopoverHeader = styled.div`
  font-family: ${wiki.fonts.ui};
  font-size: 0.72rem;
  color: ${c.textMuted};
  margin-bottom: 0.5rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const PopoverQuote = styled.div`
  font-family: ${wiki.fonts.body};
  font-size: 0.82rem;
  color: ${c.textSec};
  font-style: italic;
  padding: 0.4rem 0.6rem;
  background: ${c.accentSoft};
  border-left: 2px solid ${c.accent};
  border-radius: 0 ${wiki.radiusSm} ${wiki.radiusSm} 0;
  margin-bottom: 0.6rem;
  max-height: 60px;
  overflow: hidden;
`;

const PopoverTextarea = styled.textarea`
  width: 100%;
  min-height: 60px;
  font-family: ${wiki.fonts.ui};
  font-size: 0.82rem;
  color: ${c.text};
  background: ${c.bg};
  border: 1px solid ${c.border};
  border-radius: ${wiki.radiusSm};
  padding: 0.5rem;
  resize: vertical;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: ${c.accent};
  }
`;

const PopoverActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.4rem;
  margin-top: 0.5rem;
`;

const PopoverBtn = styled.button`
  font-family: ${wiki.fonts.ui};
  font-size: 0.72rem;
  padding: 0.3rem 0.7rem;
  border-radius: ${wiki.radiusSm};
  cursor: pointer;
  transition: all 0.2s;

  ${(p) =>
    p.$primary
      ? css`
          background: ${c.accent};
          color: #fff;
          border: 1px solid ${c.accent};
          &:hover {
            background: ${c.accentHover};
          }
        `
      : css`
          background: transparent;
          color: ${c.textSec};
          border: 1px solid ${c.border};
          &:hover {
            border-color: ${c.textSec};
          }
        `}
`;

/* ── Styled: Comments Panel ──────────────────────────────── */

const CommentsPanelWrap = styled.div`
  position: fixed;
  top: 0;
  right: ${(p) => (p.$open ? '0' : '-360px')};
  width: 340px;
  height: 100vh;
  background: ${c.bgWhite};
  border-left: 1px solid ${c.border};
  box-shadow: -4px 0 24px ${c.shadowMd};
  z-index: 150;
  transition: right 0.3s ease;
  display: flex;
  flex-direction: column;

  @media (max-width: 480px) {
    width: 100vw;
    right: ${(p) => (p.$open ? '0' : '-100vw')};
  }
`;

const CommentsPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid ${c.border};
  margin-top: ${wiki.headerHeight};
`;

const CommentsPanelTitle = styled.h3`
  font-family: ${wiki.fonts.ui};
  font-size: 0.85rem;
  font-weight: 700;
  color: ${c.text};
`;

const CommentsPanelClose = styled.button`
  background: none;
  border: none;
  font-size: 1.2rem;
  color: ${c.textMuted};
  cursor: pointer;
  padding: 0.2rem;
  line-height: 1;

  &:hover {
    color: ${c.text};
  }
`;

const CommentsPanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;
`;

const CommentCard = styled.div`
  padding: 0.75rem;
  border: 1px solid ${c.borderLight};
  border-radius: ${wiki.radiusSm};
  margin-bottom: 0.6rem;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${c.border};
  }
`;

const CommentSelectedText = styled.div`
  font-family: ${wiki.fonts.body};
  font-size: 0.78rem;
  color: ${c.textSec};
  font-style: italic;
  padding: 0.3rem 0.5rem;
  background: ${c.accentSoft};
  border-left: 2px solid ${c.accent};
  border-radius: 0 3px 3px 0;
  margin-bottom: 0.4rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CommentText = styled.p`
  font-family: ${wiki.fonts.ui};
  font-size: 0.8rem;
  color: ${c.text};
  line-height: 1.45;
  margin-bottom: 0.3rem;
`;

const CommentFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: ${wiki.fonts.ui};
  font-size: 0.68rem;
  color: ${c.textMuted};
`;

const CommentDeleteBtn = styled.button`
  background: none;
  border: none;
  color: ${c.textMuted};
  font-size: 0.82rem;
  cursor: pointer;
  padding: 0.15rem 0.3rem;
  line-height: 1;
  border-radius: 3px;
  transition: all 0.2s;

  &:hover {
    color: #c0392b;
    background: rgba(192, 57, 43, 0.08);
  }
`;

const NoComments = styled.p`
  font-family: ${wiki.fonts.ui};
  font-size: 0.82rem;
  color: ${c.textMuted};
  text-align: center;
  padding: 2rem 0;
`;

/* ── Styled: Edit Overlay ───────────────────────────────── */

const EditOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 500;
  background: ${c.bg};
  display: flex;
  flex-direction: column;
`;

const EditHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid ${c.border};
  background: ${c.bgWhite};
`;

const EditTitle = styled.span`
  font-family: ${wiki.fonts.heading};
  font-size: 1.1rem;
  font-weight: 600;
  color: ${c.text};
`;

const EditActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const EditBtn = styled.button`
  font-family: ${wiki.fonts.ui};
  font-size: 0.82rem;
  padding: 0.4rem 1rem;
  border-radius: ${wiki.radiusSm};
  cursor: pointer;
  transition: all 0.2s;

  ${(p) =>
    p.$primary
      ? css`
          background: ${c.accent};
          color: #fff;
          border: 1px solid ${c.accent};
          &:hover {
            background: ${c.accentHover};
          }
        `
      : css`
          background: transparent;
          color: ${c.textSec};
          border: 1px solid ${c.border};
          &:hover {
            border-color: ${c.textSec};
          }
        `}
`;

const EditBody = styled.div`
  flex: 1;
  overflow: auto;
  padding: 1rem 1.5rem;
`;

const EditTextarea = styled.textarea`
  width: 100%;
  height: 100%;
  min-height: 400px;
  font-family: ${wiki.fonts.mono};
  font-size: 0.9rem;
  line-height: 1.65;
  color: ${c.text};
  background: ${c.bgWhite};
  border: 1px solid ${c.border};
  border-radius: ${wiki.radiusSm};
  padding: 1.25rem;
  resize: none;
  outline: none;

  &:focus {
    border-color: ${c.accent};
  }
`;

/* ── Styled: Password Dialog ────────────────────────────── */

const DialogOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 300;
  background: ${c.overlay};
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const DialogBox = styled.div`
  background: ${c.bgWhite};
  border-radius: ${wiki.radius};
  padding: 2rem;
  max-width: 360px;
  width: 100%;
  box-shadow: 0 16px 48px ${c.shadowMd};
`;

const DialogTitle = styled.h3`
  font-family: ${wiki.fonts.heading};
  font-size: 1.25rem;
  margin-bottom: 1.25rem;
  color: ${c.text};
`;

const PwInput = styled.input`
  width: 100%;
  padding: 0.7rem 0.9rem;
  font-family: ${wiki.fonts.ui};
  font-size: 0.9rem;
  border: 1px solid ${c.border};
  border-radius: ${wiki.radiusSm};
  outline: none;
  margin-bottom: 0.5rem;
  color: ${c.text};
  background: ${c.bg};

  &:focus {
    border-color: ${c.accent};
  }
`;

const PwError = styled.p`
  font-family: ${wiki.fonts.ui};
  font-size: 0.78rem;
  color: #c0392b;
  margin-bottom: 0.5rem;
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
`;

/* ── Styled: Back to Top ────────────────────────────────── */

const BackTopBtn = styled.button`
  position: fixed;
  bottom: ${(p) => (p.$shift ? '4rem' : '1.5rem')};
  right: 4rem;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${c.bgWhite};
  border: 1px solid ${c.border};
  color: ${c.textMuted};
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: ${(p) => (p.$visible ? 0.7 : 0)};
  pointer-events: ${(p) => (p.$visible ? 'auto' : 'none')};
  transition: all 0.3s;
  z-index: 89;
  box-shadow: 0 2px 8px ${c.shadow};

  &:hover {
    opacity: 1;
    color: ${c.accent};
    border-color: ${c.accent};
  }
`;

/* ── Styled: Loading ────────────────────────────────────── */

const LoadingWrap = styled.div`
  max-width: ${wiki.maxPage};
  margin: 4rem auto;
  padding: 0 2.5rem;
`;

/* ── Sub-components ─────────────────────────────────────── */

function TableOfContents({ headings, activeId }) {
  if (!headings || headings.length === 0) {
    return (
      <TOCSide>
        <TOCSticky>
          <TOCTitle>Contents</TOCTitle>
          <p
            style={{
              fontFamily: wiki.fonts.ui,
              fontSize: '0.72rem',
              color: `var(--w-text-muted, #999)`,
              fontStyle: 'italic',
              lineHeight: 1.5,
            }}
          >
            No sections available for this article.
          </p>
        </TOCSticky>
      </TOCSide>
    );
  }

  return (
    <TOCSide>
      <TOCSticky>
        <TOCTitle>Contents</TOCTitle>
        <TOCList>
          {headings.map((h) => (
            <TOCItem key={h.id} $level={h.level} $active={h.id === activeId}>
              <a
                href={`#${h.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById(h.id)
                    ?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {h.text}
              </a>
            </TOCItem>
          ))}
        </TOCList>
      </TOCSticky>
    </TOCSide>
  );
}

function ReferencesSection({ references }) {
  if (!references || references.length === 0) return null;

  return (
    <RefsPanel>
      <RefsTitle>Sources &amp; References</RefsTitle>
      <RefList>
        {references.map((ref) => (
          <RefEntry key={ref.id} id={`wiki-ref-${ref.id}`}>
            <RefNum>{ref.id}.</RefNum>
            <span>{ref.text}</span>
          </RefEntry>
        ))}
      </RefList>
    </RefsPanel>
  );
}

function PasswordModal({ onLogin, onClose, config }) {
  const [pw, setPw] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pw === (config.adminPassword || 'bheri2026')) {
      onLogin();
    } else {
      setError(true);
      setPw('');
    }
  };

  return (
    <DialogOverlay onClick={onClose}>
      <DialogBox onClick={(e) => e.stopPropagation()}>
        <DialogTitle>Admin Access</DialogTitle>
        <form onSubmit={handleSubmit}>
          <PwInput
            type="password"
            value={pw}
            onChange={(e) => {
              setPw(e.target.value);
              setError(false);
            }}
            placeholder="Enter password"
            autoFocus
          />
          {error && <PwError>Incorrect password</PwError>}
          <DialogActions>
            <EditBtn type="button" onClick={onClose}>
              Cancel
            </EditBtn>
            <EditBtn $primary type="submit">
              Login
            </EditBtn>
          </DialogActions>
        </form>
      </DialogBox>
    </DialogOverlay>
  );
}

function EditorOverlay({ title, content, onSave, onCancel }) {
  const [text, setText] = useState(content);
  const [preview, setPreview] = useState(false);

  return (
    <EditOverlay>
      <EditHead>
        <EditTitle>Editing: {title}</EditTitle>
        <EditActions>
          <EditBtn onClick={() => setPreview((v) => !v)}>
            {preview ? 'Edit' : 'Preview'}
          </EditBtn>
          <EditBtn onClick={onCancel}>Cancel</EditBtn>
          <EditBtn $primary onClick={() => onSave(text)}>
            Save
          </EditBtn>
        </EditActions>
      </EditHead>
      <EditBody>
        {preview ? (
          <Article>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
            >
              {text}
            </ReactMarkdown>
          </Article>
        ) : (
          <EditTextarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
          />
        )}
      </EditBody>
    </EditOverlay>
  );
}

function LoadingSkeleton() {
  return (
    <LoadingWrap>
      <SkeletonLine $w="60%" $h="2.5rem" $mb="1rem" />
      <SkeletonLine $w="35%" $h="0.9rem" $mb="2.5rem" />
      <SkeletonLine $w="100%" $h="1rem" />
      <SkeletonLine $w="100%" $h="1rem" />
      <SkeletonLine $w="80%" $h="1rem" $mb="2rem" />
      <SkeletonLine $w="40%" $h="1.5rem" $mb="1rem" />
      <SkeletonLine $w="100%" $h="1rem" />
      <SkeletonLine $w="100%" $h="1rem" />
      <SkeletonLine $w="90%" $h="1rem" />
    </LoadingWrap>
  );
}

function ReviewCommentsPanel({ comments, onDelete, onClose, open }) {
  return (
    <CommentsPanelWrap $open={open}>
      <CommentsPanelHeader>
        <CommentsPanelTitle>
          Review Comments ({comments.length})
        </CommentsPanelTitle>
        <CommentsPanelClose onClick={onClose}>&times;</CommentsPanelClose>
      </CommentsPanelHeader>
      <CommentsPanelBody>
        {comments.length === 0 ? (
          <NoComments>
            No comments yet. Select text in the article to add review comments.
          </NoComments>
        ) : (
          comments.map((cmt) => (
            <CommentCard key={cmt.id}>
              <CommentSelectedText>
                &ldquo;{cmt.selectedText}&rdquo;
              </CommentSelectedText>
              <CommentText>{cmt.text || cmt.comment}</CommentText>
              <CommentFooter>
                <span>{timeAgo(cmt.timestamp)}</span>
                <CommentDeleteBtn
                  onClick={() => onDelete(cmt.id)}
                  title="Delete comment"
                >
                  &#128465;
                </CommentDeleteBtn>
              </CommentFooter>
            </CommentCard>
          ))
        )}
      </CommentsPanelBody>
    </CommentsPanelWrap>
  );
}

/* ── Upload Modal ──────────────────────────────────────── */

function UploadModal({ config, onClose }) {
  const [file, setFile] = useState(null);
  const [pages, setPages] = useState(null);
  const [parseError, setParseError] = useState('');
  const [mode, setMode] = useState('replace');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState(null);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setParseError('');
    setPages(null);
    setStatus(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (!Array.isArray(data)) throw new Error('File must contain a JSON array');
        const valid = data.every((p) => p.path && p.content);
        if (!valid) throw new Error('Each entry must have "path" and "content" fields');
        setPages(data);
      } catch (err) {
        setParseError(err.message);
      }
    };
    reader.readAsText(f);
  };

  const handleUpload = async () => {
    if (!pages || uploading) return;
    setUploading(true);
    setProgress(10);
    setStatus(null);

    try {
      setProgress(30);
      const res = await fetch('/api/import-content', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': config.adminPassword || 'bheri2026',
        },
        body: JSON.stringify({ pages, mode }),
      });

      setProgress(80);
      const data = await res.json();

      if (!res.ok) {
        setStatus({ error: true, text: data.error || 'Upload failed' });
        setProgress(0);
      } else {
        setProgress(100);
        setStatus({
          error: false,
          text: `${data.message}. Site will auto-redeploy in ~2 minutes.`,
        });
      }
    } catch (err) {
      setStatus({ error: true, text: `Network error: ${err.message}` });
      setProgress(0);
    } finally {
      setUploading(false);
    }
  };

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalBox>
        <ModalClose onClick={onClose}>&times;</ModalClose>
        <ModalTitle>Upload Content</ModalTitle>

        <FileDropZone>
          <span style={{ fontSize: '1.6rem' }}>&#128196;</span>
          <span>
            {file ? file.name : 'Click to select .txt / .json file'}
          </span>
          <input
            type="file"
            accept=".txt,.json"
            onChange={handleFile}
          />
        </FileDropZone>

        {parseError && (
          <UploadStatus $error>
            {parseError}
          </UploadStatus>
        )}

        {pages && (
          <>
            <FileInfo>
              <span>{pages.length} pages found</span>
              <span
                style={{ cursor: 'pointer', color: `var(--w-text-muted)` }}
                onClick={() => {
                  setFile(null);
                  setPages(null);
                  setParseError('');
                  setStatus(null);
                }}
              >
                &times; Clear
              </span>
            </FileInfo>

            <ModeSelector>
              <ModeOption
                $active={mode === 'replace'}
                onClick={() => setMode('replace')}
              >
                Replace All
                <br />
                <small style={{ opacity: 0.7 }}>
                  Remove old, use new
                </small>
              </ModeOption>
              <ModeOption
                $active={mode === 'merge'}
                onClick={() => setMode('merge')}
              >
                Merge
                <br />
                <small style={{ opacity: 0.7 }}>
                  Update + add new
                </small>
              </ModeOption>
            </ModeSelector>

            <UploadBtn
              onClick={handleUpload}
              disabled={uploading || (status && !status.error)}
            >
              {uploading
                ? 'Uploading...'
                : status && !status.error
                  ? 'Done!'
                  : `Upload ${pages.length} pages (${mode})`}
            </UploadBtn>

            {uploading && <ProgressBar $pct={progress} />}
          </>
        )}

        {status && (
          <UploadStatus $error={status.error}>
            {status.text}
          </UploadStatus>
        )}
      </ModalBox>
    </ModalOverlay>
  );
}

/* ── Main Component ─────────────────────────────────────── */

export default function WikiPage() {
  const { slug } = useParams();
  const { config } = useWikiConfig();
  const { page, loading, error, savePage } = useWikiPage(slug);
  const { isAdmin, login, logout } = useWikiAdmin();
  const { isProofread, toggleProofread } = useWikiProofread(slug);
  const { comments, addComment, removeComment } = useReviewComments(slug);

  const [showPwDialog, setShowPwDialog] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showBackTop, setShowBackTop] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenElapsed, setRegenElapsed] = useState(0);
  const [regenResult, setRegenResult] = useState(null);
  const [compareTab, setCompareTab] = useState('new');
  const [showCommentsPanel, setShowCommentsPanel] = useState(false);
  const [selectionInfo, setSelectionInfo] = useState(null);
  const [commentText, setCommentText] = useState('');

  const articleRef = useRef(null);
  const popoverRef = useRef(null);

  const title = slugToTitle(slug);
  const dharaLogo = config.dharaLogoUrl || '/Dhara_logo.png';

  const { body, references, headings, readingTime } = useMemo(() => {
    if (!page?.content)
      return { body: '', references: [], headings: [], readingTime: 1 };
    return processContent(page.content);
  }, [page]);

  const displayBody = useMemo(() => {
    if (!isAdmin || comments.length === 0) return body;
    let result = body;
    comments.forEach((cmt) => {
      const escaped = cmt.selectedText.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&',
      );
      result = result.replace(
        new RegExp(escaped),
        `<mark class="wiki-review-hl" title="${(cmt.text || cmt.comment || '').replace(/"/g, '&quot;')}">${cmt.selectedText}</mark>`,
      );
    });
    return result;
  }, [body, isAdmin, comments]);

  const headingIds = useMemo(() => headings.map((h) => h.id), [headings]);
  const activeHeadingId = useScrollSpy(headingIds);

  useEffect(() => {
    const handler = (e) => {
      const a = e.target.closest('a[href^="#wiki-ref-"]');
      if (a) {
        e.preventDefault();
        const target = document.querySelector(a.getAttribute('href'));
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          document
            .querySelectorAll('.wiki-ref-highlight')
            .forEach((el) => el.classList.remove('wiki-ref-highlight'));
          void target.offsetWidth;
          target.classList.add('wiki-ref-highlight');
          setTimeout(() => target.classList.remove('wiki-ref-highlight'), 2500);
        }
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  useEffect(() => {
    const onScroll = () => setShowBackTop(window.scrollY > 500);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  /* Review mode: text selection handler */
  useEffect(() => {
    if (!reviewMode) {
      setSelectionInfo(null);
      return;
    }

    const handleMouseUp = (e) => {
      if (
        popoverRef.current &&
        popoverRef.current.contains(e.target)
      )
        return;

      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || !selection.toString().trim()) {
        setSelectionInfo(null);
        return;
      }

      if (!articleRef.current) return;
      const range = selection.getRangeAt(0);
      if (!articleRef.current.contains(range.commonAncestorContainer)) return;

      const text = selection.toString().trim();
      if (text.length < 3) return;

      const rect = range.getBoundingClientRect();
      setSelectionInfo({
        text: text.substring(0, 300),
        top: Math.min(rect.bottom + 8, window.innerHeight - 250),
        left: Math.max(
          10,
          Math.min(rect.left + rect.width / 2 - 140, window.innerWidth - 300),
        ),
      });
      setCommentText('');
    };

    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [reviewMode]);

  const handleSaveComment = useCallback(() => {
    if (!selectionInfo || !commentText.trim()) return;
    addComment(selectionInfo.text, commentText.trim());
    setSelectionInfo(null);
    setCommentText('');
    window.getSelection()?.removeAllRanges();
  }, [selectionInfo, commentText, addComment]);

  const mdComponents = useMemo(
    () => ({
      h2: ({ children, ...props }) => {
        const id = textToId(getChildText(children));
        return (
          <h2 id={id} {...props}>
            {children}
          </h2>
        );
      },
      h3: ({ children, ...props }) => {
        const id = textToId(getChildText(children));
        return (
          <h3 id={id} {...props}>
            {children}
          </h3>
        );
      },
      h4: ({ children, ...props }) => {
        const id = textToId(getChildText(children));
        return (
          <h4 id={id} {...props}>
            {children}
          </h4>
        );
      },
      a: ({ href, children, ...props }) => {
        if (href && href.startsWith('/wiki/')) {
          return <Link to={href}>{children}</Link>;
        }
        if (href && href.startsWith('#')) {
          return (
            <a
              href={href}
              onClick={(e) => {
                e.preventDefault();
                const el = document.querySelector(href);
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              {...props}
            >
              {children}
            </a>
          );
        }
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
            {children}
          </a>
        );
      },
    }),
    [],
  );

  const handleEditTrigger = useCallback(() => {
    if (isAdmin) {
      setEditing(true);
    } else {
      setShowPwDialog(true);
    }
  }, [isAdmin]);

  const handleLogin = useCallback(() => {
    login();
    setShowPwDialog(false);
  }, [login]);

  const handleDeletePage = useCallback(async () => {
    if (!window.confirm(`Delete "${slugToTitle(slug)}" from the wiki? This will also exclude it from future rebuilds.`)) return;
    setDeleting(true);
    try {
      const res = await fetch('/api/delete-page', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': config.adminPassword || 'bheri2026',
        },
        body: JSON.stringify({ slug }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(`Delete failed: ${data.error}`);
      } else {
        alert('Page deleted. Site will update in ~2 minutes.');
        window.location.href = config.wikiBasePath || '/wiki';
      }
    } catch (err) {
      alert(`Network error: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  }, [slug, config]);

  useEffect(() => {
    if (!regenerating) { setRegenElapsed(0); return; }
    const t = setInterval(() => setRegenElapsed((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [regenerating]);

  const handleRegenerate = useCallback(async () => {
    if (!window.confirm(`Regenerate "${slugToTitle(slug)}"? This may take 5–10 minutes.`)) return;
    const oldContent = page?.content || '';
    setRegenerating(true);
    try {
      const updated = await regeneratePage(config, slug);
      setRegenResult({ oldContent, newContent: updated.content });
      setCompareTab('new');
    } catch (err) {
      if (err.message === 'already_in_progress') {
        alert('Regeneration is already in progress for this page. Please wait.');
      } else {
        alert(`Regeneration failed: ${err.message}`);
      }
    } finally {
      setRegenerating(false);
    }
  }, [slug, config, page]);

  const handleAcceptNew = useCallback(() => {
    if (regenResult) savePage(regenResult.newContent);
    setRegenResult(null);
  }, [regenResult, savePage]);

  const handleKeepOriginal = useCallback(() => {
    setRegenResult(null);
  }, []);

  const handleSave = useCallback(
    (newContent) => {
      savePage(newContent);
      setEditing(false);
    },
    [savePage],
  );

  const toggleReviewMode = useCallback(() => {
    setReviewMode((v) => {
      if (!v) setShowCommentsPanel(true);
      return !v;
    });
  }, []);

  /* ── Render ─────────────────────────────────────────── */

  if (loading) return <LoadingSkeleton />;
  if (error || !page) return <WikiNotFound />;

  return (
    <PageWrap>
      <PageHero>
        <PageHeroInner>
          <Breadcrumb>
            <Link to="/wiki">Wiki</Link>
            <Sep>/</Sep>
            <span>{title}</span>
          </Breadcrumb>

          <TitleRow>
            <PageTitle>{title}</PageTitle>
            <StatusBadge $approved={isProofread}>
              <StatusIcon>{isProofread ? '✓' : '⚠'}</StatusIcon>
              {isProofread
                ? config.proofreadText || 'Verified'
                : 'Not Proofread'}
            </StatusBadge>
          </TitleRow>
          <MetaLine>
            <span>{readingTime} min read</span>
            <MetaDot>&middot;</MetaDot>
            <span>{isProofread ? 'Expert Reviewed' : 'Auto-generated'}</span>
            <MetaDot>&middot;</MetaDot>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.3rem',
              }}
            >
              Powered by{' '}
              <DharaLogoInline src={dharaLogo} alt="Dhara" />
            </span>
          </MetaLine>

          {!isProofread && (
            <NotProofreadBanner>
              <BannerIcon>⚠</BannerIcon>
              <span>
                {config.notProofreadText ||
                  'This content was auto-generated and has not yet been proofread by subject experts.'}
              </span>
            </NotProofreadBanner>
          )}
        </PageHeroInner>
      </PageHero>

      {reviewMode && (
        <ReviewBar>
          <span>
            📝 Review Mode — Select text in the article to add comments
          </span>
          <ReviewBarBtn onClick={() => setShowCommentsPanel(true)}>
            View Comments ({comments.length})
          </ReviewBarBtn>
          <ReviewBarBtn onClick={toggleReviewMode}>Exit Review</ReviewBarBtn>
        </ReviewBar>
      )}

      <ContentLayout>
        <TableOfContents headings={headings} activeId={activeHeadingId} />

        <ContentMain>
          <Article ref={articleRef}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={mdComponents}
            >
              {displayBody}
            </ReactMarkdown>
          </Article>

          <ArticleEnd>
            <EndMark>&#x2767;</EndMark>
          </ArticleEnd>

          <ReferencesSection references={references} />
        </ContentMain>
      </ContentLayout>

      {/* Admin: edit trigger (visible when not logged in) */}
      {!isAdmin && (
        <EditTrigger onClick={handleEditTrigger} title="Admin login">
          &#9998; Admin
        </EditTrigger>
      )}

      {/* Admin bar (when logged in) */}
      {isAdmin && !editing && (
        <AdminBar>
          <AdminBtn onClick={() => setEditing(true)}>&#9998; Edit</AdminBtn>
          <AdminDivider />
          <ApproveBtn $approved={isProofread} onClick={toggleProofread}>
            {isProofread ? '✓ Approved' : '○ Approve'}
          </ApproveBtn>
          <AdminBtn onClick={toggleReviewMode}>
            📝 {reviewMode ? 'Exit Review' : 'Proofread'}
            {comments.length > 0 && (
              <ReviewBadge>{comments.length}</ReviewBadge>
            )}
          </AdminBtn>
          <AdminBtn
            disabled={regenerating}
            onClick={handleRegenerate}
            title="Regenerate this article using the knowledge system (takes 5–10 min)"
          >
            {regenerating ? '↻ Regenerating…' : '↻ Regenerate'}
          </AdminBtn>
          <AdminDivider />
          <AdminBtn
            onClick={handleDeletePage}
            disabled={deleting}
            style={{ color: '#e57373' }}
          >
            {deleting ? '...' : '✕ Delete Page'}
          </AdminBtn>
          <AdminDivider />
          <AdminBtn onClick={logout}>Logout</AdminBtn>
        </AdminBar>
      )}

      {/* Selection comment popover */}
      {selectionInfo && reviewMode && (
        <SelectionPopover
          ref={popoverRef}
          style={{
            top: selectionInfo.top,
            left: selectionInfo.left,
          }}
        >
          <PopoverHeader>Add Review Comment</PopoverHeader>
          <PopoverQuote>
            {selectionInfo.text.length > 80
              ? selectionInfo.text.substring(0, 80) + '…'
              : selectionInfo.text}
          </PopoverQuote>
          <PopoverTextarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Type your comment..."
            autoFocus
          />
          <PopoverActions>
            <PopoverBtn
              onClick={() => {
                setSelectionInfo(null);
                window.getSelection()?.removeAllRanges();
              }}
            >
              Cancel
            </PopoverBtn>
            <PopoverBtn
              $primary
              onClick={handleSaveComment}
              disabled={!commentText.trim()}
            >
              Save
            </PopoverBtn>
          </PopoverActions>
        </SelectionPopover>
      )}

      {/* Comments panel */}
      <ReviewCommentsPanel
        comments={comments}
        onDelete={removeComment}
        onClose={() => setShowCommentsPanel(false)}
        open={showCommentsPanel}
      />

      {/* Back to top */}
      <BackTopBtn
        $visible={showBackTop}
        $shift={isAdmin && !editing}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        title="Back to top"
      >
        &uarr;
      </BackTopBtn>

      {/* Regeneration overlay */}
      {regenerating && (
        <RegenOverlay>
          <RegenSpinner />
          <RegenTitle>Regenerating Article</RegenTitle>
          <RegenMsg>
            Our knowledge system is regenerating this article.
            This usually takes 5–10 minutes. Please keep this tab open.
          </RegenMsg>
          <RegenTimer>
            {Math.floor(regenElapsed / 60)}:{String(regenElapsed % 60).padStart(2, '0')} elapsed
          </RegenTimer>
        </RegenOverlay>
      )}

      {/* Comparison view after regeneration */}
      {regenResult && (
        <CompareOverlay>
          <CompareHeader>
            <CompareTitle>Review Regenerated Article</CompareTitle>
            <CompareActions>
              <CompareBtn onClick={handleKeepOriginal}>
                ✕ Keep Original
              </CompareBtn>
              <CompareBtn $primary onClick={handleAcceptNew}>
                ✓ Accept New Version
              </CompareBtn>
            </CompareActions>
          </CompareHeader>
          <CompareTabs>
            <CompareTab
              $active={compareTab === 'new'}
              onClick={() => setCompareTab('new')}
            >
              Regenerated Version
            </CompareTab>
            <CompareTab
              $active={compareTab === 'old'}
              onClick={() => setCompareTab('old')}
            >
              Previous Version
            </CompareTab>
          </CompareTabs>
          <CompareBody>
            <CompareLabel $new={compareTab === 'new'}>
              {compareTab === 'new' ? (
                <>
                  <CompareBadge $new>New</CompareBadge> Regenerated content
                </>
              ) : (
                <>
                  <CompareBadge>Old</CompareBadge> Previous content
                </>
              )}
            </CompareLabel>
            <div className="wiki-compare-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
              >
                {compareTab === 'new'
                  ? regenResult.newContent
                  : regenResult.oldContent}
              </ReactMarkdown>
            </div>
          </CompareBody>
        </CompareOverlay>
      )}

      {/* Password dialog */}
      {showPwDialog && (
        <PasswordModal
          config={config}
          onLogin={handleLogin}
          onClose={() => setShowPwDialog(false)}
        />
      )}

      {showUploadModal && (
        <UploadModal
          config={config}
          onClose={() => setShowUploadModal(false)}
        />
      )}

      {/* Edit overlay */}
      {editing && (
        <EditorOverlay
          title={title}
          content={page.content}
          onSave={handleSave}
          onCancel={() => setEditing(false)}
        />
      )}
    </PageWrap>
  );
}
